import { AlertCircle, RotateCcw } from 'lucide-react';
import { forwardRef, type HTMLAttributes, type MouseEvent } from 'react';

import { cn } from '@heidi/utils';

import { Button } from '../button';
import { useIsExperimentalDesignSystemForms } from '../hooks/use-is-experimental-design-system-forms';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../tooltip';
import { FileCardDeleteButton } from './file-card-delete-button';
import { hasValidFileExtension } from './file-card-utils';
import { fileCardErrorToneClassesExperimental } from './file-card.experimental.styles';
import { fileCardErrorToneClasses } from './file-card.styles';

export interface FileCardErrorProps extends HTMLAttributes<HTMLDivElement> {
  /** File name to display */
  fileName?: string;
  /** File type/extension (e.g. "PDF", "DOCX") */
  fileType?: string;
  /** Error message for tooltip */
  errorMessage?: string;
  /** Label for the failed status text (default: "Failed") */
  failedLabel?: string;
  /** Fallback tooltip text when no errorMessage is provided (default: "Upload failed") */
  fallbackErrorMessage?: string;
  /** Size variant */
  size?: 'default' | 'mobile';
  /** Delete callback */
  onDelete?: (e: MouseEvent<HTMLButtonElement>) => void;

  /**
   * Accessible label for the delete control, named after the file it removes.
   * Supplied by `FileCard` so the message lives in one place.
   */
  deleteAriaLabel?: string;
  /** Retry callback */
  onRetry?: (e: MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Error variant of FileCard shown when a file upload fails.
 * Displays a red border, error icon with tooltip, file name, retry button, and delete button.
 */
export const FileCardError = forwardRef<HTMLDivElement, FileCardErrorProps>(
  (
    {
      fileName,
      fileType,
      errorMessage,
      failedLabel = 'Failed',
      fallbackErrorMessage = 'Upload failed',
      size = 'default',
      onDelete,
      deleteAriaLabel,
      onRetry,
      className,
      ...props
    },
    ref
  ) => {
    const isMobileSize = size === 'mobile';
    const cardClasses = isMobileSize
      ? 'h-auto w-[204px] gap-2 rounded-xl py-1.5 ps-1.5 pe-2.5'
      : 'h-10 w-44 gap-2.5 rounded-lg py-1 ps-1 pe-2';
    const iconContainerClasses = isMobileSize
      ? 'size-10 rounded-lg'
      : 'size-8 rounded-md';
    const iconClasses = isMobileSize ? 'size-6' : 'size-4';
    const textContainerClasses = isMobileSize
      ? 'gap-2 justify-center pt-0'
      : 'gap-0 pt-0.5';
    const fileNameClasses = isMobileSize
      ? 'text-sm leading-none'
      : 'text-xs leading-tight';
    const fileTypeClasses = isMobileSize
      ? 'text-xs leading-none'
      : 'text-[10px] leading-tight';

    const displayFileName =
      fileName && hasValidFileExtension(fileName)
        ? fileName.slice(0, fileName.lastIndexOf('.'))
        : fileName;
    const displayFileType = fileType || 'Unknown';

    const isExperimentalForms = useIsExperimentalDesignSystemForms();
    const tone = isExperimentalForms
      ? fileCardErrorToneClassesExperimental
      : fileCardErrorToneClasses;

    return (
      <div
        ref={ref}
        data-experimental-form={isExperimentalForms}
        className={cn(
          'group relative flex items-center border bg-surface shadow-sm',
          tone.container,
          cardClasses,
          className
        )}
        {...props}
      >
        {/* Error icon with tooltip - only the icon triggers the tooltip */}
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  'flex aspect-square shrink-0 items-center justify-center overflow-hidden',
                  tone.iconFrame,
                  iconContainerClasses
                )}
              >
                <AlertCircle
                  className={cn(tone.icon, iconClasses)}
                  aria-label={fallbackErrorMessage}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">
              {errorMessage || fallbackErrorMessage}
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {/* File info */}
        <div
          className={cn(
            'flex min-w-0 flex-1 flex-col overflow-hidden',
            textContainerClasses
          )}
        >
          <p
            className={cn(
              'min-w-0 self-stretch truncate font-medium text-text-primary',
              fileNameClasses
            )}
          >
            {displayFileName}
          </p>
          <p className={cn('truncate font-normal', tone.meta, fileTypeClasses)}>
            {displayFileType} · {failedLabel}
          </p>
        </div>
        {/* Retry Button */}
        {onRetry && (
          <Button
            iconOnly
            size="xxs"
            variant="ghost"
            className="shrink-0 text-text-secondary hover:text-text-primary"
            onClick={(e) => {
              e.stopPropagation();
              onRetry(e);
            }}
            onKeyDown={(e) => e.stopPropagation()}
            aria-label={`Retry upload of ${fileName}`}
          >
            <RotateCcw className={iconClasses} />
          </Button>
        )}
        {/* Delete Button */}
        {onDelete && (
          <FileCardDeleteButton
            onDelete={onDelete}
            ariaLabel={deleteAriaLabel}
            alwaysVisible={isMobileSize}
          />
        )}
      </div>
    );
  }
);

FileCardError.displayName = 'FileCardError';
